import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Eye, 
  MoreHorizontal,
  CheckCircle,
  X,
  Clock,
  Shield,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building,
  UserCheck,
  UserX,
  AlertTriangle,
  Download,
  Upload,
  RefreshCw,
  Settings,
  Key,
  Lock,
  Unlock,
  UserPlus,
  UserMinus,
  Crown,
  Star,
  Target,
  Activity,
  BarChart3,
  PieChart,
  TrendingUp
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import { FormField, FormModal, useFormValidation } from '../../components/forms/FormComponents';
import { validateUserForm, sanitizeInput } from '../../utils/formValidation';
import ConfirmationModal from '../../components/modals/ConfirmationModal';

const UserManagement = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    department: 'all',
    location: 'all',
    lastLoginDays: 'all',
    createdDays: 'all'
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getUserManagement();
        if (response.success) {
          setUserData(response.data);
        } else {
          setError('Failed to load user data');
        }
      } catch (err) {
        setError('Error loading user data');
        console.error('User data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleCloseUserForm = () => {
    setShowUserForm(false);
    setSelectedUser(null);
  };

  const handleSubmitUser = async (formData) => {
    try {
      setLoading(true);
      
      // Validate form data
      const validation = validateUserForm(formData);
      if (!validation.isValid) {
        const errorMessage = Object.values(validation.errors).join(', ');
        alert(`Validation failed: ${errorMessage}`);
        return;
      }
      
      // Sanitize input data
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        phone: sanitizeInput(formData.phone || ''),
        role: formData.role,
        department: sanitizeInput(formData.department),
        location: sanitizeInput(formData.location || ''),
        status: formData.status || 'active'
      };
      
      let response;
      
      if (selectedUser) {
        // Update existing user
        response = await MockDataService.updateUser(selectedUser.id, sanitizedData);
      } else {
        // Create new user
        response = await MockDataService.createUser(sanitizedData);
      }
      
      if (response.success) {
        // Refresh user data
        const userResponse = await MockDataService.getUserManagement();
        if (userResponse.success) {
          setUserData(userResponse.data);
        }
        
        // Show success message (we'll implement toast notifications later)
        alert(selectedUser ? 'User updated successfully!' : 'User created successfully!');
        
        setShowUserForm(false);
        setSelectedUser(null);
      } else {
        alert(`Error: ${response.message}`);
      }
    } catch (error) {
      console.error('Error submitting user:', error);
      alert('An error occurred while saving the user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      
      // Handle bulk delete
      if (Array.isArray(userToDelete)) {
        let successCount = 0;
        let errorCount = 0;
        
        for (const userId of userToDelete) {
          try {
            const response = await MockDataService.deleteUser(userId);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }
        
        alert(`Bulk delete completed: ${successCount} successful, ${errorCount} failed`);
      } else {
        // Handle single delete
        const response = await MockDataService.deleteUser(userToDelete);
        
        if (response.success) {
          alert('User deleted successfully!');
        } else {
          alert(`Error: ${response.message}`);
        }
      }
      
      // Refresh user data
      const userResponse = await MockDataService.getUserManagement();
      if (userResponse.success) {
        setUserData(userResponse.data);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('An error occurred while deleting the user');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setSelectedUsers([]);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      setLoading(true);
      const response = await MockDataService.updateUser(userId, { status: newStatus });
      
      if (response.success) {
        // Refresh user data
        const userResponse = await MockDataService.getUserManagement();
        if (userResponse.success) {
          setUserData(userResponse.data);
        }
        alert(`User status updated to ${newStatus}!`);
      } else {
        alert(`Error: ${response.message}`);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('An error occurred while updating user status');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Operations
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const userId of selectedUsers) {
        try {
          let response;
          switch (action) {
            case 'activate':
              response = await MockDataService.updateUser(userId, { status: 'active' });
              break;
            case 'deactivate':
              response = await MockDataService.updateUser(userId, { status: 'inactive' });
              break;
            case 'delete':
              response = await MockDataService.deleteUser(userId);
              break;
            default:
              continue;
          }
          
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      // Refresh data
      const userResponse = await MockDataService.getUserManagement();
      if (userResponse.success) {
        setUserData(userResponse.data);
      }

      alert(`Bulk operation completed: ${successCount} successful, ${errorCount} failed`);
      setSelectedUsers([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error('Bulk operation error:', error);
      alert('An error occurred during bulk operation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading user management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No user data available</p>
        </div>
      </div>
    );
  }

  // The service returns { users, roles, departments } directly
  // Handle both possible structures for compatibility
  const users = userData.users || userData.userManagement?.users || [];
  const userRoles = userData.roles || userData.userRoles || userData.userManagement?.roles || [];
  const departments = userData.departments || userData.userManagement?.departments || [];
  
  // Compute userStats from users if not provided
  const userStats = userData.userStats || {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    pendingUsers: users.filter(u => u.status === 'pending').length,
    admins: users.filter(u => u.role === 'admin' || u.role === 'super_admin').length
  };
  
  // Get recentUsers if provided, otherwise derive from users
  const recentUsers = userData.recentUsers || 
    [...users].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.lastLogin || 0);
      const dateB = new Date(b.createdAt || b.lastLogin || 0);
      return dateB - dateA;
    }).slice(0, 5);
  
  // Get userAnalytics if provided
  const userAnalytics = userData.userAnalytics || {};
  
  // Get userFilters if provided
  const userFilters = userData.userFilters || userData.userManagement?.userFilters || {};
  
  // Get userSettings if provided
  const userSettings = userData.userSettings || userData.userManagement?.userSettings || {};

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'roles', label: 'Roles', icon: Shield },
    { id: 'departments', label: 'Departments', icon: Building },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'inactive': return 'text-gray-600 dark:text-gray-400';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400';
      case 'suspended': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return X;
      case 'pending': return Clock;
      case 'suspended': return AlertTriangle;
      default: return Clock;
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return Crown;
      case 'agent': return UserCheck;
      case 'end_user': return Users;
      case 'manager': return Star;
      default: return Users;
    }
  };

  const filteredUsers = users.filter(user => {
    // Basic search
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.department?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Basic filters
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    // Advanced filters
    const matchesDepartment = advancedFilters.department === 'all' || user.department === advancedFilters.department;
    const matchesLocation = advancedFilters.location === 'all' || user.location === advancedFilters.location;
    
    // Date filters
    const now = new Date();
    const lastLoginDate = user.lastLogin ? new Date(user.lastLogin) : null;
    const createdDate = user.createdAt ? new Date(user.createdAt) : null;
    
    let matchesLastLogin = true;
    if (advancedFilters.lastLoginDays !== 'all' && lastLoginDate) {
      const daysDiff = Math.floor((now - lastLoginDate) / (1000 * 60 * 60 * 24));
      matchesLastLogin = daysDiff <= parseInt(advancedFilters.lastLoginDays);
    }
    
    let matchesCreated = true;
    if (advancedFilters.createdDays !== 'all' && createdDate) {
      const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      matchesCreated = daysDiff <= parseInt(advancedFilters.createdDays);
    }
    
    return matchesSearch && matchesStatus && matchesRole && matchesDepartment && 
           matchesLocation && matchesLastLogin && matchesCreated;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  User Management
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage users, roles, and permissions
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Upload className="h-4 w-4" />
                <span>Import</span>
              </button>
              <button
                onClick={handleCreateUser}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{userStats.totalUsers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{userStats.activeUsers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{userStats.pendingUsers}</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Admins</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{userStats.admins}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">User Management</h3>
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Users</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </select>
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Roles</option>
                        <option value="admin">Admin</option>
                        <option value="agent">Agent</option>
                        <option value="end_user">End User</option>
                        <option value="manager">Manager</option>
                      </select>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="name">Sort by Name</option>
                        <option value="email">Sort by Email</option>
                        <option value="role">Sort by Role</option>
                        <option value="lastLogin">Sort by Last Login</option>
                      </select>
                    </div>
                  </div>

                  {/* Bulk Actions Bar */}
                  {selectedUsers.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                          </span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleBulkAction('activate')}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            >
                              Activate
                            </button>
                            <button
                              onClick={() => handleBulkAction('deactivate')}
                              className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                            >
                              Deactivate
                            </button>
                            <button
                              onClick={() => {
                                setUserToDelete(selectedUsers);
                                setShowDeleteConfirm(true);
                              }}
                              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedUsers([])}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Users List */}
                  <div className="space-y-3">
                    {/* Select All Header */}
                    <div className="flex items-center space-x-4 p-4 bg-gray-100 dark:bg-gray-600 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select All ({filteredUsers.length} users)
                      </span>
                    </div>
                    
                    {filteredUsers.map((user) => {
                      const StatusIcon = getStatusIcon(user.status);
                      const RoleIcon = getRoleIcon(user.role);
                      return (
                        <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleSelectUser(user.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</h3>
                                <div className="flex items-center space-x-1">
                                  <RoleIcon className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{user.role}</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                                <div className="flex items-center space-x-1">
                                  <Mail className="h-3 w-3" />
                                  <span>{user.email}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Building className="h-3 w-3" />
                                  <span>{user.department}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>Last login: {new Date(user.lastLogin).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <StatusIcon className={`h-4 w-4 ${getStatusColor(user.status)}`} />
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              user.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                              user.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                              user.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                              'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                            }`}>
                              {user.status}
                            </span>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                              >
                                <Edit3 className="h-4 w-4 text-gray-400" />
                              </button>
                              <button
                                onClick={() => handleToggleUserStatus(user.id, user.status)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                              >
                                {user.status === 'active' ? (
                                  <UserX className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <UserCheck className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                              >
                                <Trash2 className="h-4 w-4 text-gray-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Roles Tab */}
              {activeTab === 'roles' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Roles & Permissions</h2>
                    <button className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
                      <Plus className="h-4 w-4" />
                      <span>Add Role</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {userRoles.map((role) => (
                      <div key={role.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                              <Shield className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{role.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{role.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{role.userCount} users</span>
                            <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                              <Edit3 className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {role.permissions.map((permission) => (
                            <div key={permission.id} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">{permission.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Departments Tab */}
              {activeTab === 'departments' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Departments</h2>
                    <button className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
                      <Plus className="h-4 w-4" />
                      <span>Add Department</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {departments.map((department) => (
                      <div key={department.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Building className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{department.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{department.description}</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Users:</span>
                            <span className="text-gray-900 dark:text-gray-100">{department.userCount}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Manager:</span>
                            <span className="text-gray-900 dark:text-gray-100">{department.manager}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Location:</span>
                            <span className="text-gray-900 dark:text-gray-100">{department.location}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User Analytics</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <Download className="h-4 w-4" />
                        <span>Export Report</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <PieChart className="h-8 w-8 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">User Distribution</p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">By Role</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {userAnalytics.roleDistribution.map((role) => (
                          <div key={role.role} className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{role.role}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{role.count} ({role.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <TrendingUp className="h-8 w-8 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Activity Trends</p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Last 30 Days</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">New Users</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{userAnalytics.newUsers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Active Users</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{userAnalytics.activeUsers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Login Rate</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{userAnalytics.loginRate}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {userAnalytics.recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{activity.description}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {activity.user} • {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          activity.type === 'success' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                          activity.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                          'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        }`}>
                          {activity.type}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[112]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedUser ? 'Edit User' : 'Create New User'}
              </h3>
              <button
                onClick={handleCloseUserForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    defaultValue={selectedUser?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    defaultValue={selectedUser?.email || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="end_user">End User</option>
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Department
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="engineering">Engineering</option>
                    <option value="support">Support</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="hr">Human Resources</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  defaultValue={selectedUser?.phone || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseUserForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubmitUser({})}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                {selectedUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title={Array.isArray(userToDelete) ? "Delete Users" : "Delete User"}
        message={Array.isArray(userToDelete) 
          ? `Are you sure you want to delete ${userToDelete.length} users? This action cannot be undone.`
          : "Are you sure you want to delete this user? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default UserManagement;
